import { SKIP_VALUE } from './mapping-phase-parts';
import {
  columnHasUnmatchedSample,
  capturedValueForBlank,
  getPatternPlaceholderNumbers,
} from './mapping-phase-transform-editor';
import type { ColumnMappingItem, FieldOptionItem } from './mapping-phase-types';

export interface MappingRowBlankView {
  index: number;
  target: string | null;
  capturedValue: string;
}

export interface MappingRowView {
  isFanOut: boolean;
  isCleaned: boolean;
  isSkipped: boolean;
  isUnknown: boolean;
  currentValue: string;
  matchedOption: FieldOptionItem | undefined;
  hasWarning: boolean;
  cleanedValue: string;
  /** The single real placeholder number for an isCleaned row (targets is padded/indexed by number). */
  singlePlaceholderNumber: number;
  blanks: MappingRowBlankView[];
}

/** Narrows a mapping's transform to a pattern transform, or undefined for constant/valueMap/none. */
function asPatternTransform(mapping: ColumnMappingItem) {
  return mapping.transform?.type === 'pattern' ? mapping.transform : undefined;
}

function computeCurrentValue(
  mapping: ColumnMappingItem,
  transform: ReturnType<typeof asPatternTransform>,
  isCleaned: boolean,
  singlePlaceholderNumber: number,
): string {
  // targets is indexed by placeholder NUMBER, not row position — a skip-numbered
  // single-placeholder pattern like `{2}` stores its target at index 1, not index 0.
  if (isCleaned) return transform!.targets[singlePlaceholderNumber - 1] ?? SKIP_VALUE;
  return mapping.mappedTo ?? SKIP_VALUE;
}

/** Skip/unknown highlighting only applies to a single-select row — a fanned-out row has no row-level selection. */
function computeSelectionFlags(
  isFanOut: boolean,
  currentValue: string,
  matchedOption: FieldOptionItem | undefined,
): { isSkipped: boolean; isUnknown: boolean } {
  if (isFanOut) return { isSkipped: false, isUnknown: false };
  return {
    isSkipped: currentValue === SKIP_VALUE,
    isUnknown: currentValue !== SKIP_VALUE && matchedOption == null,
  };
}

/**
 * Fan-out child rows, one per ACTUAL placeholder present in the pattern — NOT one per
 * `targets` array slot. `targets` is padded/indexed by placeholder NUMBER (see
 * applyPatternTextChange), so a skip-numbered pattern like `{1}|{3}` has a length-3
 * `targets` array with a phantom slot at index 1 for the non-existent `{2}`. Rendering
 * directly off `targets.length` would fan out a blank row for that phantom slot too.
 */
function computeBlanks(
  transform: ReturnType<typeof asPatternTransform>,
  isFanOut: boolean,
  sampleValues: string[],
): MappingRowBlankView[] {
  if (!isFanOut || !transform) return [];
  return getPatternPlaceholderNumbers(transform.pattern).map((placeholderNumber) => ({
    index: placeholderNumber,
    target: transform.targets[placeholderNumber - 1] ?? null,
    capturedValue: capturedValueForBlank(transform.pattern, sampleValues, placeholderNumber),
  }));
}

export function computeMappingRowView(
  mapping: ColumnMappingItem,
  fieldOptions: FieldOptionItem[],
  sampleValues: string[],
): MappingRowView {
  const transform = asPatternTransform(mapping);
  // Actual placeholder numbers, not the padded targets.length — targets is indexed by
  // placeholder NUMBER (see computeBlanks' doc comment above), so a skip-numbered
  // single-placeholder pattern like `{2}` alone has a length-2 targets array but only
  // 1 real blank.
  const placeholderNumbers = transform ? getPatternPlaceholderNumbers(transform.pattern) : [];
  const blankCount = placeholderNumbers.length;
  const isFanOut = blankCount >= 2;
  const isCleaned = blankCount === 1;
  const singlePlaceholderNumber = placeholderNumbers[0] ?? 1;
  const currentValue = computeCurrentValue(mapping, transform, isCleaned, singlePlaceholderNumber);
  const matchedOption = fieldOptions.find((o) => o.key === currentValue);
  const { isSkipped, isUnknown } = computeSelectionFlags(isFanOut, currentValue, matchedOption);
  const hasWarning = transform != null && columnHasUnmatchedSample(transform.pattern, sampleValues);
  const cleanedValue = isCleaned
    ? capturedValueForBlank(transform!.pattern, sampleValues, singlePlaceholderNumber)
    : '';
  const blanks = computeBlanks(transform, isFanOut, sampleValues);
  return {
    isFanOut,
    isCleaned,
    isSkipped,
    isUnknown,
    currentValue,
    matchedOption,
    hasWarning,
    cleanedValue,
    singlePlaceholderNumber,
    blanks,
  };
}
